import * as React from 'react';

import {
    DurationUnit,
    GlanceData,
    GlanceMetaState,
    GlanceTime,
    Image,
    Partner,
    Region,
    RelativeTime,
    StudioAppConfig,
} from 'glance-datatypes';
import { GridList, GridTile } from 'material-ui';

import GlanceTimeComponent from './GlanceTime';
import { TimeZone } from './Clock';
import Utils from '../Utils';

const GIONEE_PARTNER_ID = '59458435b8034886b62dbe672e010e064ab5aecf';

const styles = {
    width50: {
        width: '50%',
        maxWidth: 'none',
    },
    width35: {
        width: '35%',
        maxWidth: 'none',
    },
    width30: {
        width: '30%',
        maxWidth: 'none',
    },
    width100: {
        width: '100%',
        maxWidth: 'none',
    },
    inputStyle: {
        color: '#505050',
        fontSize: '0.9em',
    },
    textareaStyle: {
        color: '#505050',
        fontSize: '0.9em',
    },
    dataTimeStyle: {
        display: 'inline-block',
        width: '50%',
    },
    title: {
        color: 'rgba(0, 0, 0, 0.3)',
        fontSize: '12px',
    },
    subtitle: {
        fontSize: '12px',
        borderBottom: '1px solid #eee',
        minHeight: '18px',
    },
};

export const durationMapping = {
    0: 'Hours',
    1: 'Days',
    2: 'Minutes',
    3: 'Seconds',
}

export const sessionModeMapping = {
    0: 'ANY',
    1: 'LOCKSCREEN',
    2: 'BINGE',
    3: 'HIGHLIGHTS',
    4: 'IN-GAME REWARDED VIDEOS',
    5: 'GAME SPLASH',
    6: 'SCREENSAVER',
    7: 'PATCHWALL_STORIES',
};

const USER_NETWORK_TYPE_SELECTED = {
    0: 'OFFLINE',
    1: 'WIFI',
    2: 'MOBILE DATA',
    'OFFLINE': 'OFFLINE',
    'WIFI': 'WIFI',
    'MOBILE_DATA': 'MOBILE DATA'
};

function getImageAttribution(image: Image): string {
    if (image && image.attribution) {
        const attribution = image.attribution;
        let result = '';
        if (attribution.author) {
            result = attribution.author + '/';
        }
        return '© ' + result + attribution.source;
    }

    return null;
}

const getUserLevelExpiry = (meta) => {
    if (meta && meta.glanceTime) {
        const { userLevelDuration } = meta.glanceTime;
        if (userLevelDuration) {
            return Utils.getUserLevelExpiry(userLevelDuration);
        }
    }
    return 'NA';

};
const getStartedAt = (date) => {
    if (date) {
        const minutes = date.getMinutes();
        const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
        const month = date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
        const year = date.getFullYear();
        const dateString = `${day}-${month}-${year}`;
        return `${dateString} ${date.getHours()}:${minutes > 9 ? minutes : `0${minutes}`}`;
    }
};



interface Props {
    glance: any;
    editable?: boolean;
    detailedView?: boolean;
    region: Region;
    partner: Partner;
    studioAppConfig?: StudioAppConfig;
}

class GlanceDetailsCard extends React.Component<Props, {}> {
    constructor(props: Props) {
        super(props);
    }

    getSegmentNames = (ids) => {
        if (ids) {
            const { studioAppConfig } = this.props;
            if (studioAppConfig && studioAppConfig.sqlSegments) {
                const { sqlSegments } = studioAppConfig;
                return sqlSegments.filter(sqlsegment => ids.includes(sqlsegment.id)).map(sqlsegment => `${sqlsegment.name}/${sqlsegment.displayName}`).join(', ');
            }
        }
        return 'NA';
    }

    getPartnerExperiments = (partnerExperiments) => {
        let partnerExpInfo = '';
        if (partnerExperiments) {
            partnerExperiments.forEach(partnerExperiment => {
                const { partnerId, experiments } = partnerExperiment;
                const partnerName = this.getPartnerName(partnerId);
                partnerExpInfo = `${partnerExpInfo && `${partnerExpInfo},`} ${this.getExperimentDetails(experiments)}(${partnerName}) `;
            });
            return partnerExpInfo;
        }
        return 'NA';
    }

    getPartnerName = (partnerId) => {
        const { partners } = this.props.studioAppConfig;
        if (partners) {
            const partnerObj = partners.find(partner => partner.partnerId === partnerId);
            if (partnerObj) {
                return partnerObj.name;
            }
        }
    }

    getExperimentDetails = (experimentArray) => {
        if (experimentArray) {
            const { studioAppConfig } = this.props;
            if (studioAppConfig && studioAppConfig.experiments) {
                const experimentNames = [];
                const { experiments } = studioAppConfig;
                experimentArray.forEach(expArray => {
                    const expObj = experiments.find(exp => exp.id === expArray.id);
                    if (expObj) {
                        const variantObj = expObj.variants.find(variant => variant.id === expArray.variantId);
                        if (variantObj) {
                            experimentNames.push(`${variantObj.name}/${expObj.name}`);
                        } else if (expObj.control.id === expArray.variantId) {
                            experimentNames.push(`${expObj.control.name}/${expObj.name}`);
                        }
                    }
                });

                return experimentNames.join(', ');
            }
        }
        return 'NA';
    }

    render() {
        const partners = [];
        let isMetaArray: boolean = false;
        if ('partnerIds' in this.props.glance.meta) {
            isMetaArray = true;
            const partnerIdNames = this.props.studioAppConfig.partners.filter(partner => {
                return this.props.glance.meta.partnerIds.indexOf(partner.partnerId) > -1;
            });
            partnerIdNames.forEach((partner) => {
                partners.push(partner.name);
            });
        }
        const partnerNames = partners.join(', ');
        const data: GlanceData = this.props.glance.data;
        const meta = this.props.glance.meta;
        const editable: boolean = this.props.editable;
        const partnerName = (this.props.partner && this.props.partner.name )|| '';

        const getCtaDetail = (data, key) => {
          if (data && data.peek && data.peek.article) {
            const { cta } = data.peek.article;
            if (partnerName === 'Transsion') {
              const modifiedCtaUrl = Utils.updateUrlParams(cta ? cta[key] : 'NA', {utm_medium: 'trans'});
              return modifiedCtaUrl;
            }
            return cta && cta[key] ? cta[key] : 'NA';
          } else if (data && data.peek && data.peek.video) {
            const { cta } = data.peek.video;
            if (this.props.partner.name === 'Transsion') {
              const modifiedCtaUrl = Utils.updateUrlParams(cta ? cta[key] : 'NA', {utm_medium: 'trans'});
              return modifiedCtaUrl;
            }
            return cta && cta[key] ? cta[key] : 'NA';
          }
          return 'NA';
        };

        let elements = [];

        elements.push(
            <GridTile cols={data.glanceLanguageId != null ? (isMetaArray ? 0.33 : 0.5)
                : (isMetaArray ? 0.5 : 1)} rows={1}>
                <div style={styles.title}>Glance Id</div>
                <div style={styles.subtitle}>{data.glanceId}</div>
            </GridTile>,
        );
        if (data.glanceLanguageId != null) {
            elements.push(
                <GridTile cols={isMetaArray ? 0.33 : 0.5} rows={1}>
                    <div style={styles.title}>Language</div>
                    <div style={styles.subtitle}>{data.glanceLanguageId}</div>
                </GridTile>,

            );
        }

        let priorityVal = "";
        switch (data.priority) {
            case 0:
                priorityVal = "Normal Priority";
                break;
            case 1:
                priorityVal = "High Priority 1";
                break;
            case 2:
                priorityVal = "High Priority 2";
                break;
            default:
                break;
        }
        if (isMetaArray) {
            elements.push(
                <GridTile cols={data.glanceLanguageId ? 0.33 : 0.5} rows={1}>
                    <div style={styles.title}>Priority</div>
                    <div style={styles.subtitle}>{priorityVal}</div>
                </GridTile>,

            );
        }
        let categories: string = data.categoryIds ? data.categoryIds.join(', ') : '';
        elements.push(
            <GridTile cols={1} rows={1}>
                <div style={styles.title}>Categories</div>
                <div style={styles.subtitle}>{categories}</div>
            </GridTile>,

        );

        elements.push(
            <GridTile cols={data.bingeGlanceId != null ? 0.5 : 1} rows={1}>
                <div style={styles.title}>Mode View</div>
                {data && Utils.getRenderSessionData(data).map((renderSession, index, arr = []) => {
                    return (
                        <span style={styles.subtitle}>{`${sessionModeMapping[renderSession]}${(index === (arr.length - 1)) ? '' : ','}  `}</span>
                    )
                })}
            </GridTile>,

        );

        if (data.bingeGlanceId != null) {
            elements.push(
                <GridTile cols={0.5} rows={1}>
                    <div style={styles.title}>Binge Glance Id</div>
                    <div style={styles.subtitle}>{data.bingeGlanceId}</div>
                </GridTile>,
            );
        }

        if (data.peekData) {
            elements.push(
                <GridTile cols={1} rows={1}>
                    <div style={styles.title}>Glance Title</div>
                    <div style={styles.subtitle}>{this.props.partner && this.props.partner.partnerId == GIONEE_PARTNER_ID && data.peekData.shortTitle ? data.peekData.shortTitle : data.peekData.title}</div>
                </GridTile>,

            );
            elements.push(
                <GridTile cols={data.overlayGlanceId != null ? 0.5 : 1} rows={1}>
                    <div style={styles.title}>Peek Source Name</div>
                    <div style={styles.subtitle}>{data.peekData.sourceName}</div>
                </GridTile>,

            );
            if (data.bubbleId != null) {
                elements.push(
                    <GridTile cols={isMetaArray ? 0.5 : 1} rows={1}>
                        <div style={styles.title}>BubbleId</div>
                        <div style={styles.subtitle}>{data.bubbleId}</div>
                    </GridTile>,

                );
            }
            if (data.overlayGlanceId != null) {
                elements.push(
                    <GridTile cols={0.5} rows={1}>
                        <div style={styles.title}>Overlay Glance Id</div>
                        <div style={styles.subtitle}>{data.overlayGlanceId}</div>
                    </GridTile>,

                );
            }
            if (isMetaArray) {
                elements.push(
                    <GridTile cols={1} rows={1}>
                        <div style={styles.title}>Partners</div>
                        <div style={styles.subtitle}>{partnerNames}</div>
                    </GridTile>,
                );
                if (meta.internalRejectionReason) {
                    elements.push(
                        <GridTile cols={1} rows={1}>
                            <div style={styles.title}>RejectionReason</div>
                            <div style={styles.subtitle}>{meta.internalRejectionReason}</div>
                        </GridTile>,
                    );
                }
            }

            if (this.props.detailedView) {
                let summary;
                let offlinePeek = 'NA';
                if (data.peek) {
                    if (data.peek.article && data.peek.article.summary) {
                        summary = this.props.partner &&
                            this.props.partner.partnerId == GIONEE_PARTNER_ID &&
                            data.peek.article.cheesySummary ? data.peek.article.cheesySummary : data.peek.article.summary;
                    } else if (data.peek.video && data.peek.video.summary) {
                        summary = this.props.partner &&
                            this.props.partner.partnerId == GIONEE_PARTNER_ID &&
                            data.peek.video.cheesySummary ? data.peek.video.cheesySummary : data.peek.video.summary;
                    }
                }
                if (data.offlinePeek) {
                    if (data.offlinePeek.article) {
                        offlinePeek = data.offlinePeek.article.cta.originalUrl;
                    } else {
                        offlinePeek = data.offlinePeek.video.cta.originalUrl;
                    }
                }
                elements.push(
                    <GridTile cols={1} rows={1}>
                        <div style={styles.title}>Summary</div>
                        <div style={styles.subtitle}>{summary}</div>
                    </GridTile>,
                    <GridTile cols={1} rows={1}>
                        <div style={styles.title}>Attribution</div>
                        <div style={styles.subtitle}>{
                            getImageAttribution(data.image)
                        }</div>
                    </GridTile>,
                    <GridTile cols={0.5} rows={1}>
                        <div style={styles.title}>Priority</div>
                        <div style={styles.subtitle}>{priorityVal}</div>
                    </GridTile>,
                    <GridTile cols={0.5} rows={1}>
                        <div style={styles.title}>Publisher</div>
                        <div style={styles.subtitle}>{
                            data.publisherData ? data.publisherData.displayName : 'NA'
                        }</div>
                    </GridTile>,
                    <GridTile cols={0.5} rows={1}>
                        <div style={styles.title}>Daily Cap</div>
                        <div style={styles.subtitle}>{
                            data.frequencyCaps ? data.frequencyCaps.daily : 0
                        }</div>
                    </GridTile>,
                    <GridTile cols={0.5} rows={1}>
                        <div style={styles.title}>Weekly Cap</div>
                        <div style={styles.subtitle}>{
                            data.frequencyCaps ? data.frequencyCaps.weekly : 0
                        }</div>
                    </GridTile>,
                    <GridTile cols={1} rows={1}>
                        <div style={styles.title}>Offline Peek</div>
                        <div style={styles.subtitle}>{
                            offlinePeek
                        }</div>
                    </GridTile>,
                    <GridTile cols={0.5} rows={1}>
                        <div style={styles.title}>Session Mode</div>
                        <div style={styles.subtitle}>
                            {data && Utils.getRenderSessionData(data).map((renderSession, index, arr = []) => {
                                return (<span style={styles.subtitle}>{`${sessionModeMapping[renderSession]}${(index === (arr.length - 1)) ? '' : ','}  `}</span>)
                            })}
                        </div>
                    </GridTile>,
                    <GridTile cols={0.5} rows={1}>
                        <div style={styles.title}>StickiNess</div>
                        <div style={styles.subtitle}>{
                            data.stickinessCount ? data.stickinessCount : '0'
                        }</div>
                    </GridTile>
                    ,
                    <GridTile cols={0.5} rows={1}>
                        <div style={styles.title}>Cta Text</div>
                        <div style={styles.subtitle}>{
                            getCtaDetail(data, 'text')
                        }</div>
                    </GridTile>,
                    <GridTile cols={0.5} rows={1}>
                        <div style={styles.title}>User Level Expiry</div>
                        <div style={styles.subtitle}>{
                            getUserLevelExpiry(meta)
                        }</div>
                    </GridTile>,
                    <GridTile cols={1} rows={1}>
                        <div style={styles.title}>Cta Url</div>
                        <div style={styles.subtitle}>{
                            getCtaDetail(data, 'url')
                        }</div>
                    </GridTile>,
                    <GridTile cols={1} rows={1}>
                        <div style={styles.title}>Positive Targeted Segments Users</div>
                        <div style={styles.subtitle}>{
                            this.getSegmentNames(data.segmentIds)
                        }</div>
                    </GridTile>,
                    <GridTile cols={1} rows={1}>
                        <div style={styles.title}>Negative Targeted Segments Users</div>
                        <div style={styles.subtitle}>{
                            this.getSegmentNames(data.antiSegmentIds)
                        }</div>
                    </GridTile>, <GridTile cols={1} rows={1}>
                    <div style={styles.title}>Experiments</div>
                    <div style={styles.subtitle}>{
                        meta.partnerExperiments ? this.getPartnerExperiments(meta.partnerExperiments) :
                            this.getExperimentDetails(meta.experiments)
                    }</div>
                </GridTile>,
                );

            }
        }

        let selectedNetworkTypes = [];
        if (data.userNetworkTypes) {
            data.userNetworkTypes.forEach((element, key) => {
                key === data.userNetworkTypes.length - 1
                    ? selectedNetworkTypes.push(USER_NETWORK_TYPE_SELECTED[element])
                    : selectedNetworkTypes.push(`${USER_NETWORK_TYPE_SELECTED[element]}, `)
            });
            elements.push(
                <GridTile cols={1} rows={1}>
                    <div style={styles.title}>Network Targeting</div>
                    <div style={styles.subtitle}>{selectedNetworkTypes}</div>
                </GridTile>,
            );
        }

        const rules = meta.ruleIdsList;
        if (rules && rules.length) {
            const segmentTargetingRuleMapping = Object.assign(
                {}, ...this.props.studioAppConfig.segmentTargetingRule.map(item => ({ [item.ruleId]: item.name }))
            );

            const targetingRulesDetailList = [];
            rules.map((rule) => {
                const targetingRulesDetail = rule.ruleIdlist.map(ruleId => segmentTargetingRuleMapping[ruleId]).filter(item => !!item).join(', ');
                targetingRulesDetailList.push(targetingRulesDetail);
            })

            if (targetingRulesDetailList.length) {
                elements.push(
                    <GridTile cols={1} rows={1}>
                        <div style={styles.title}>Targeting Rules</div>
                        {
                            targetingRulesDetailList.map((targetingRule) => {
                                return <div style={styles.subtitle}>{targetingRule}</div>
                            })
                        }
                    </GridTile>,
                );
            }
        }

        const glanceTime: GlanceTime = meta.glanceTime;
        let startDate: Date = Utils.getStartDate(this.props.region, glanceTime);
        let startEpochFinalInSecs: number = Utils.getEpochFinalInSecs(glanceTime);
        elements.push(
            <GridTile cols={0.5} rows={1}>
                <div style={styles.title}>Started at</div>
                <div style={styles.subtitle}>{getStartedAt(startDate)}</div>
            </GridTile>,
            <GridTile cols={0.5} rows={1}>
                <div style={styles.title}>{meta.glanceTime.duration.unit == DurationUnit.DAYS ? 'Duration in Days' : `Duration in ${durationMapping[meta.glanceTime.duration.unit]}`}</div>
                <div style={styles.subtitle}>{meta.glanceTime.duration.length}</div>
            </GridTile>,
            <GridTile cols={0.5} rows={1}>
                {meta.state === GlanceMetaState.STUDIO_PAUSED ?
                    <div>
                        <div style={styles.title}>Paused at</div>
                        <div style={styles.subtitle}>{ Utils.getReadableAbsoluteTime(meta.pausedAtInSecs)}</div>
                    </div>
                    :null}
            </GridTile>,
            <GridTile cols={1} rows={1}>
                <div key='glance_time' style={{ ...styles.width100, display: 'inline-block' }}>
                    <GlanceTimeComponent startEpochInSecs={startEpochFinalInSecs} endEpochInSecs={startEpochFinalInSecs + (Utils.getMillis(glanceTime.duration) / 1000)} />
                </div>
            </GridTile>,
        );

        return (
            <div>
                <GridList cols={1} cellHeight={'auto'}>
                    {
                        elements
                    }
                </GridList>
            </div>
        );
    }
}

export default GlanceDetailsCard;



// WEBPACK FOOTER //
// ./src/components/GlanceDetailsCard.tsx